import React from 'react';
import {
	Box,
	Grid,
	Typography,
	TextField,
	InputAdornment,
	IconButton,
	MenuItem,
	Checkbox,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Control, Controller } from 'react-hook-form';
import { RiEyeFill, RiEyeOffFill } from 'react-icons/ri';
import { LocationWithContact, Languages, LocationVisibleChannel } from 'common/types';

import { useTranslation } from 'services/localization';
import HasFeature from 'components/features/HasFeature';

interface Props {
	formControl: Control<LocationWithContact>;
	defaultLanguage: Languages;
}

type ChannelOptions = LocationVisibleChannel | 'ALL';
const defaultVisibleChannels: LocationVisibleChannel[] = ['ADMIN', 'ONLINE', 'STORE'];

const StoreInformation = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();

	return (
		<Box>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Typography variant="h6" gutterBottom>
						{t('store.about', 'About')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="name"
								render={(renderProps) => (
									<TextField
										label={t('store.storeName', 'Store name')}
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
										fullWidth
										variant="outlined"
									/>
								)}
							/>
						</Grid>
						<HasFeature requiredFeature="CHANNELS">
							<Grid item xs={12}>
								<Controller
									control={props.formControl}
									name="channels"
									render={(renderProps) => {
										const mapVisibleChannelsValueToLabel: Record<ChannelOptions, string> = {
											ALL: t('product.allChannels'),
											ADMIN: t('product.adminChannel'),
											ONLINE: t('product.onlineChannel'),
											STORE: t('product.selfServiceChannel'),
										};
										const renderVisibleChannelsValue = (selected: ChannelOptions[]) => {
											if (!selected.length) {
												return t('product.hiddenChannel');
											}
											return selected
												.map((value) => mapVisibleChannelsValueToLabel[value])
												.join(', ');
										};

										const handleChannelsChange = (
											e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
										) => {
											const value = e.target.value as ChannelOptions[];
											const lastAddedItem = value[value.length - 1];
											let selectedChannels: LocationVisibleChannel[] | undefined;
											if (lastAddedItem === 'ALL') {
												renderProps.field.onChange(defaultVisibleChannels);
											} else {
												selectedChannels = value.includes('ALL')
													? defaultVisibleChannels.filter((c) => c !== lastAddedItem)
													: (value as LocationVisibleChannel[]);
												if (selectedChannels.length === defaultVisibleChannels.length) {
													renderProps.field.onChange(defaultVisibleChannels);
												} else {
													renderProps.field.onChange(selectedChannels);
												}
											}
										};
										const visibleChannels: ChannelOptions[] = renderProps.field.value ?? ['ALL'];

										return (
											<TextField
												select
												name="channels"
												label={t('common:states.visibility', 'Visibility')}
												fullWidth
												variant="outlined"
												value={visibleChannels}
												onChange={handleChannelsChange}
												SelectProps={{
													multiple: true,
													renderValue: (e) => renderVisibleChannelsValue(e as ChannelOptions[]),
													MenuProps: {
														className: classes.selectMenu,
													},
													displayEmpty: true,
												}}
												InputLabelProps={{
													shrink: true,
												}}
											>
												<MenuItem
													value="ALL"
													classes={{ selected: classes.selected }}
													className={classes.allSelect}
												>
													<Checkbox
														icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
														color="primary"
														checkedIcon={<CheckBoxIcon fontSize="small" />}
														style={{ marginRight: 8 }}
														checked={visibleChannels.includes('ALL')}
													/>
													<Typography variant="body1" color="textSecondary">
														{t('common:actions.selectAll', 'Select all')}
													</Typography>
												</MenuItem>
												{defaultVisibleChannels.map((value) => (
													<MenuItem
														key={value}
														value={value}
														classes={{ selected: classes.selected }}
													>
														<Checkbox
															icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
															color="primary"
															checkedIcon={<CheckBoxIcon fontSize="small" />}
															style={{ marginRight: 8 }}
															checked={
																visibleChannels.includes(value) || visibleChannels.includes('ALL')
															}
														/>
														<Typography variant="body1">
															{mapVisibleChannelsValueToLabel[value]}
														</Typography>
													</MenuItem>
												))}
											</TextField>
										);
									}}
								/>
							</Grid>
						</HasFeature>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Box mt={4} />
				</Grid>
				<Grid item xs={12} md={6}>
					<Typography variant="h6" gutterBottom>
						{t('store.address', 'Address')}
					</Typography>
					<Typography variant="body2">
						{t('store.addressDescription', 'Help your customers find your store')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="address"
								render={(renderProps) => (
									<TextField
										label={t('common:form.streetAddress', 'Street address')}
										placeholder={t('common:form.streetAddressPlaceholder', '425 Grove Street')}
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
										fullWidth
										variant="outlined"
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="city"
								render={(renderProps) => (
									<TextField
										label={t('common:form.city', 'City')}
										placeholder={t('common:form.cityPlaceholder', 'New York')}
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
										fullWidth
										variant="outlined"
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="zipCode"
								render={(renderProps) => (
									<TextField
										label={t('common:form.postalCode', 'Postal code')}
										placeholder="10001"
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
										fullWidth
										variant="outlined"
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="additionalInformation"
								defaultValue={{ def: '' }}
								render={(renderProps) => (
									<TextField
										label={t('common:form.additionalInformation', 'Additional information')}
										placeholder={t(
											'common:form.addressAdditionalInformationPlaceholder',
											'Entrance through the back door / Please wear a mask when visiting / ...',
										)}
										value={renderProps.field.value?.def ?? ''}
										onChange={(e) =>
											renderProps.field.onChange({
												...renderProps.field.value,
												def: e.target.value,
												[props.defaultLanguage]: e.target.value,
											})
										}
										fullWidth
										variant="outlined"
										multiline
										rows={5}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Box mt={4} />
				</Grid>
				<Grid item xs={12} md={6}>
					<Typography variant="h6" gutterBottom>
						{t('store.contactDetails', 'Contact details')}
					</Typography>
					<Typography variant="body2">
						{t('store.contactDetailsDescription', 'If your customers need to get in touch')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={6}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="email"
								render={(emailProps) => (
									<Controller
										control={props.formControl}
										name="hideEmail"
										defaultValue={false}
										render={(hideEmailProps) => (
											<TextField
												label={t('common:form.emailAddress')}
												placeholder={t(
													'common:form.storeEmailAddressPlaceholder',
													'hello@store.com',
												)}
												value={emailProps.field.value}
												onChange={(e) => emailProps.field.onChange(e.target.value)}
												fullWidth
												variant="outlined"
												helperText={
													hideEmailProps.field.value
														? t(
																'store.contactDetailHidden',
																'Hidden (except in receipts and confirmation emails)',
														  )
														: t('store.contactDetailVisible', 'Visible to everyone')
												}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																onClick={() =>
																	hideEmailProps.field.onChange(!hideEmailProps.field.value)
																}
																size="large"
															>
																{hideEmailProps.field.value ? (
																	<RiEyeOffFill color={theme.palette.action.disabled} />
																) : (
																	<RiEyeFill color={theme.palette.primary.main} />
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								)}
							/>
							<Box mt={2} />
						</Grid>
						<Grid item xs={12}>
							<Controller
								control={props.formControl}
								name="phone"
								render={(phoneProps) => (
									<Controller
										control={props.formControl}
										name="hidePhone"
										defaultValue={false}
										render={(hidePhoneProps) => (
											<TextField
												label={t('common:form.phoneNumber')}
												placeholder={t('common:form.phoneNumberPlaceholder', '+358 123 4567')}
												value={phoneProps.field.value}
												onChange={(e) => phoneProps.field.onChange(e.target.value)}
												fullWidth
												variant="outlined"
												helperText={
													hidePhoneProps.field.value
														? t(
																'store.contactDetailHidden',
																'Hidden (except in receipts and confirmation emails)',
														  )
														: t('store.contactDetailVisible', 'Visible to everyone')
												}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																onClick={() =>
																	hidePhoneProps.field.onChange(!hidePhoneProps.field.value)
																}
																size="large"
															>
																{hidePhoneProps.field.value ? (
																	<RiEyeOffFill color={theme.palette.action.disabled} />
																) : (
																	<RiEyeFill color={theme.palette.primary.main} />
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
											/>
										)}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		selectMenu: {
			zIndex: '1400 !important' as any,
		},
		selected: {
			background: 'transparent !important',
		},
		allSelect: {
			paddingLeft: theme.spacing(1),
			borderBottom: `1px solid ${theme.palette.divider}`,
		},
	}),
);

export default StoreInformation;
